import resolveModule from '/native/importMetaResolve.mjs'

/**
 * Workers can only be spawned from files that are on the hosted domain.
 * By bootstrapping a worker, and letting it import the actual worker module, we can work around this limitation
 * and still have the worker logic in a separate mfe while maintaining CSP-compliancy.
 * @param {string} scriptUrl - Either the full url to the worker module script, or a relative URL with an `mfeKey`. If omitted, you have to handle the bootstrap messaging yourself.
 * @param {string} mfeKey - The key of the MFE to resolve the scriptUrl against. Required if scriptUrl is a relative URL.
 * @returns Promise<{Worker}> A promise that resolves to the spawned Worker instance, once the worker module has been loaded and is ready to receive messages.
 */
export default async function spawnWebWorker(scriptUrl = '', mfeKey = null) {
    const resolvedWorkerPath = resolveModule('/native/workerBootstrap.mjs')
    const worker = new Worker(resolvedWorkerPath, { type: 'module' })
    if (!scriptUrl) {
        return worker
    }

    return new Promise((resolve, reject) => {
        const failureTimeout = 10_000
        let timeoutRef = null
        worker.addEventListener('message', e => {
            if (e.data.message === 'loaded') {
                clearTimeout(timeoutRef)
                resolve(worker)
            }
        })
        worker.postMessage({
            moduleUrl: mfeKey
                ? resolveModule(`${mfeKey}${scriptUrl}`)
                : scriptUrl,
        })

        timeoutRef = setTimeout(() => {
            reject(
                new Error(
                    `Worker failed to load script within ${failureTimeout}ms`
                )
            )
        }, failureTimeout)
    })
}
